// Firebase.ts

// --- Core Firebase Imports ---
import { initializeApp, FirebaseApp } from "firebase/app";
import { getAnalytics, setAnalyticsCollectionEnabled, Analytics } from "firebase/analytics";
import { getStorage, FirebaseStorage } from 'firebase/storage';
import { getFirestore, Firestore } from 'firebase/firestore';

// --- Auth Resilience Imports ---
import { 
  Auth,
  initializeAuth, 
  browserLocalPersistence, 
  browserSessionPersistence,
  inMemoryPersistence
} from "firebase/auth";

// Your existing config remains the same
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_apiKey,
  authDomain: process.env.REACT_APP_FIREBASE_authDomain,
  projectId: process.env.REACT_APP_FIREBASE_projectId,
  storageBucket: process.env.REACT_APP_FIREBASE_storageBucket,
  messagingSenderId: process.env.REACT_APP_FIREBASE_messagingSenderId,
  appId: process.env.REACT_APP_FIREBASE_appId,
  measurementId: process.env.REACT_APP_FIREBASE_measurementId
};

// --- Core App Initialization (Unchanged) ---
const app: FirebaseApp = initializeApp(firebaseConfig);
const analytics: Analytics = getAnalytics(app);

setAnalyticsCollectionEnabled(analytics, false);

const storage: FirebaseStorage = getStorage(app);
const firestore: Firestore = getFirestore(app);

// --- Auth Resilience Logic ---

/**
 * Checks if IndexedDB is available and not blocked/hung.
 * @param {number} timeout - The maximum time to wait in milliseconds.
 * @returns {Promise<boolean>} - True if IndexedDB is available, false otherwise.
 */
const isIndexedDbAvailable = (timeout = 300): Promise<boolean> => {
  return new Promise((resolve) => {
    const timeoutId = setTimeout(() => {
      console.warn("IndexedDB availability check timed out.");
      resolve(false);
    }, timeout);

    try {
      const dbRequest = indexedDB.open("firebase-health-check");
      dbRequest.onsuccess = (event) => {
        clearTimeout(timeoutId);
        if (event.target && (event.target as IDBOpenDBRequest).result) {
            (event.target as IDBOpenDBRequest).result.close();
        }
        resolve(true);
      };
      dbRequest.onerror = () => {
        clearTimeout(timeoutId);
        console.error("IndexedDB is unavailable or restricted in this environment.");
        resolve(false);
      };
    } catch (error) {
      clearTimeout(timeoutId);
      console.error("An exception occurred while checking for IndexedDB availability.", error);
      resolve(false);
    }
  });
};

/**
 * Checks if Session Storage is available and writable.
 * @returns {boolean} - True if Session Storage is available, false otherwise.
 */
const isSessionStorageAvailable = (): boolean => {
    try {
        const testKey = 'firebase-session-storage-test';
        sessionStorage.setItem(testKey, 'test');
        sessionStorage.removeItem(testKey);
        return true;
    } catch (e) {
        console.warn("Session Storage is unavailable or restricted.");
        return false;
    }
};

// --- MODIFIED EXPORT STRATEGY ---

export let FIREBASE_AUTH: Auth;

/**
 * Initializes Firebase services with a three-tiered fallback for Auth persistence.
 * This MUST be called and awaited at the start of the application (in index.tsx).
 * @returns {Promise<void>}
 */
export const initializeFirebase = async (): Promise<void> => {
  if (FIREBASE_AUTH) {
    return;
  }

  let persistence;

  // 1. Try for IndexedDB (long-term persistence)
  if (await isIndexedDbAvailable()) {
    persistence = browserLocalPersistence;
    console.log("Firebase Auth: Using IndexedDB for local persistence.");
  } 
  // 2. If that fails, try for Session Storage (persists for the session)
  else if (isSessionStorageAvailable()) {
    persistence = browserSessionPersistence;
    console.warn("Firebase Auth: IndexedDB unavailable. Falling back to session persistence. User will be logged out when the browser is closed.");
  } 
  // 3. As a last resort, use in-memory (no persistence)
  else {
    persistence = inMemoryPersistence;
    console.error("Firebase Auth: Both IndexedDB and Session Storage are unavailable. Falling back to in-memory persistence. User will be logged out on page reload.");
  }
  
  // Populate the exported FIREBASE_AUTH variable with the chosen persistence.
  FIREBASE_AUTH = initializeAuth(app, { persistence });
};

// --- UNCHANGED EXPORTS ---
export { firestore, storage, analytics, app as default };
export const FIREBASE_APP = app;
export const FIREBASE_FIRESTORE = firestore;